import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Typography,
} from '@mui/material';
import api from '../../../utils/api';
import { StellantisContract, StellantisEligibility } from '../types';

interface Props {
	vehicleId: number;
	eligibility?: StellantisEligibility | null;
	contract?: StellantisContract | null;
	onUpdate: () => void;
}

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

interface StatusInfo {
	label: string;
	color: ChipColor;
	detail: string;
}

const formatExpiry = (iso: string | null | undefined): string => {
	if (!iso) return '';
	const d = new Date(iso);
	return d.toLocaleDateString();
};

// The api wrapper returns the raw response body under `error`, which for these
// endpoints is `{ error: "<message>" }`. It can also be a bare string
// ('cant_reach_server') or DRF field errors `{ field: ["msg"] }`. Pull a
// human-readable string out of any of those shapes so the UI never renders a
// raw object (e.g. "[object Object]").
const extractApiError = (
	error: string | string[] | Record<string, unknown> | undefined,
	fallback: string,
): string => {
	if (!error) return fallback;
	if (typeof error === 'string') {
		return error === 'cant_reach_server'
			? 'Cannot reach server. Please try again.'
			: error;
	}
	if (Array.isArray(error)) {
		return error.join(' ') || fallback;
	}
	const candidate = error.error ?? error.detail ?? error.message;
	if (typeof candidate === 'string') return candidate;
	if (Array.isArray(candidate) && typeof candidate[0] === 'string') {
		return candidate.join(' ');
	}
	const first = Object.values(error)[0];
	if (typeof first === 'string') return first;
	if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
	return fallback;
};

const resolveStatus = (
	eligibility: StellantisEligibility | null | undefined,
	contract: StellantisContract | null | undefined,
): StatusInfo => {
	if (!eligibility) {
		return {
			label: 'Not checked',
			color: 'default',
			detail: 'Eligibility has not been checked yet.',
		};
	}
	if (!eligibility.isEligible) {
		return {
			label: 'Not eligible',
			color: 'error',
			detail:
				eligibility.lastError ||
				'Mobilisights reports this VIN is not eligible for Stellantis telematics. No tracking is available for this vehicle through Stellantis.',
		};
	}
	if (!contract) {
		return {
			label: 'Eligible — not registered',
			color: 'info',
			detail: 'Vehicle is eligible. Enable to create a 360-day contract.',
		};
	}
	const status = contract.status;
	switch (status) {
		case 'pending':
			return {
				label: 'Pending',
				color: 'warning',
				detail: 'Contract registered with Mobilisights. Awaiting activation.',
			};
		case 'activating':
			return {
				label: 'Activating',
				color: 'warning',
				detail: 'Mobilisights is activating the contract.',
			};
		case 'activated':
			return {
				label: 'Active — tracking',
				color: 'success',
				detail: 'Contract activated. Telemetry should flow into Stellantis Events.',
			};
		case 'cancelling':
			return {
				label: 'Cancelling',
				color: 'warning',
				detail: 'Cancellation in progress.',
			};
		case 'cancelled':
			return {
				label: 'Cancelled',
				color: 'default',
				detail: 'Contract cancelled.',
			};
		case 'failed':
			return {
				label: 'Failed',
				color: 'error',
				detail: 'Contract creation/activation failed at Mobilisights.',
			};
		default:
			return {
				label: status || 'Unknown',
				color: 'default',
				detail: '',
			};
	}
};

const StellantisFleetRegistration: React.FC<Props> = ({
	vehicleId,
	eligibility,
	contract,
	onUpdate,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const status = resolveStatus(eligibility, contract);

	const handleEnable = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await api.post(`/vehicles/${vehicleId}/enable-stellantis/`);
			if (response.error) {
				setError(extractApiError(response.error, 'Failed to enable Stellantis.'));
			} else {
				onUpdate();
			}
		} catch {
			setError('Failed to enable Stellantis. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleDisable = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await api.post(`/vehicles/${vehicleId}/disable-stellantis/`);
			if (response.error) {
				setError(extractApiError(response.error, 'Failed to disable Stellantis.'));
			} else {
				onUpdate();
			}
		} catch {
			setError('Failed to disable Stellantis. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const expiryLine = contract?.expiresAt
		? `Cycle expires ${formatExpiry(contract.expiresAt)}`
		: '';

	const canEnable = !!eligibility?.isEligible && (!contract || !contract.isLocallyActive);
	const canDisable = !!contract?.isLocallyActive;

	return (
		<Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
				<Typography variant="body1" sx={{ fontWeight: 600 }}>
					Stellantis Tracking status:
				</Typography>
				<Chip label={status.label} color={status.color} size="small" />
				{contract?.deviceType && (
					<Chip label={contract.deviceType} size="small" variant="outlined" />
				)}
				{contract?.provider && (
					<Chip label={contract.provider} size="small" variant="outlined" />
				)}
			</Box>
			{status.detail && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{status.detail}
				</Typography>
			)}
			{expiryLine && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{expiryLine}
				</Typography>
			)}
			{contract?.contractId && (
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
					Contract ID: {contract.contractId}
				</Typography>
			)}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}
			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				{canEnable && (
					<Button
						variant="contained"
						color="primary"
						onClick={handleEnable}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={16} /> : null}
					>
						{loading ? 'Enabling…' : 'Enable Stellantis Location Services'}
					</Button>
				)}
				{canDisable && (
					<Button
						variant="outlined"
						onClick={handleDisable}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={16} /> : null}
					>
						{loading ? 'Disabling…' : 'Disable Stellantis Location Services'}
					</Button>
				)}
			</Box>
			{eligibility?.isEligible && !contract && (
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
					Enabling creates a 360-day contract that auto-cancels at day 360 to avoid annual rebilling.
				</Typography>
			)}
		</Box>
	);
};

export default StellantisFleetRegistration;
